import { apiFetch } from "../utils/apiUtils";

export type FeatureFlagKey = string;

export type FeatureFlagsEnabledMap = Record<string, boolean>;

/**
 * Feature Flag Service - fetches feature flags from the API
 */
class FeatureFlagService {
    private static instance: FeatureFlagService;

    public static getInstance(): FeatureFlagService {
        if (!FeatureFlagService.instance) {
            FeatureFlagService.instance = new FeatureFlagService();
        }
        return FeatureFlagService.instance;
    }

    /**
     * List all feature flags with their enabled states
     */
    async listWithEnabled(): Promise<{ keys: FeatureFlagKey[]; enabledMap: FeatureFlagsEnabledMap }> {
        try {
            const response = await apiFetch("/api/v1/feature_flags", {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const keys: string[] = [];
            const enabledMap: FeatureFlagsEnabledMap = {};

            const addFromRecord = (rec: Record<string, unknown>) => {
                let key: string | undefined;
                if (typeof rec.key === "string") {
                    key = rec.key;
                } else if (typeof rec.name === "string") {
                    key = rec.name;
                } else if (typeof rec.id === "string") {
                    key = rec.id;
                } else if (typeof rec.id === "number") {
                    key = String(rec.id);
                } else {
                    key = undefined;
                }
                if (key) {
                    keys.push(key);
                    if (typeof rec.enabled === "boolean") {
                        enabledMap[key] = rec.enabled;
                    }
                }
            };

            if (Array.isArray(data)) {
                data.forEach(item => {
                    if (typeof item === "string") {
                        keys.push(item);
                    } else if (item && typeof item === "object") {
                        addFromRecord(item as Record<string, unknown>);
                    }
                });
            } else if (data && typeof data === "object") {
                const obj = data as Record<string, unknown>;
                const featureFlags = (obj as { feature_flags?: unknown[] }).feature_flags;
                const flagsArray = (obj as { flags?: unknown[] }).flags;
                if (Array.isArray(featureFlags)) {
                    featureFlags.forEach(item => {
                        if (typeof item === "string") {
                            keys.push(item);
                        } else if (item && typeof item === "object") {
                            addFromRecord(item as Record<string, unknown>);
                        }
                    });
                } else if (Array.isArray(flagsArray)) {
                    flagsArray.forEach(item => {
                        if (typeof item === "string") keys.push(item);
                    });
                } else {
                    Object.keys(obj).forEach(k => keys.push(k));
                }
            }

            return { keys, enabledMap };
        } catch (error) {
            console.error("Failed to fetch feature flags:", error);
            return { keys: [], enabledMap: {} };
        }
    }

    /**
     * List all feature flag keys
     */
    async list(): Promise<FeatureFlagKey[]> {
        const { keys } = await this.listWithEnabled();
        return keys;
    }

    /**
     * Get enabled state for specific feature flags
     */
    async getEnabled(keys: FeatureFlagKey[]): Promise<FeatureFlagsEnabledMap> {
        if (!keys || keys.length === 0) {
            return {};
        }

        try {
            const response = await apiFetch(
                `/api/v1/feature_flags/enabled?keys=${keys.join(",")}`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data && typeof data === "object" && !Array.isArray(data)) {
                const obj = data as Record<string, unknown>;
                const flags = (obj as { flags?: unknown }).flags;
                if (flags && typeof flags === "object" && !Array.isArray(flags)) {
                    const map: FeatureFlagsEnabledMap = {};
                    Object.entries(flags as Record<string, unknown>).forEach(([k, v]) => {
                        map[k] = Boolean(v);
                    });
                    return map;
                }
                const enabled = (obj as { enabled?: unknown }).enabled;
                if (enabled && typeof enabled === "object" && !Array.isArray(enabled)) {
                    const map: FeatureFlagsEnabledMap = {};
                    Object.entries(enabled as Record<string, unknown>).forEach(([k, v]) => {
                        map[k] = Boolean(v);
                    });
                    return map;
                }
                const direct: FeatureFlagsEnabledMap = {};
                Object.entries(obj).forEach(([k, v]) => {
                    direct[k] = Boolean(v);
                });
                return direct;
            }

            if (Array.isArray((data as { enabled?: unknown[] })?.enabled)) {
                const enabledKeys = new Set<string>(
                    ((data as { enabled?: unknown[] }).enabled || []).filter(
                        (x): x is string => typeof x === "string"
                    )
                );
                return keys.reduce<FeatureFlagsEnabledMap>((acc, key) => {
                    acc[key] = enabledKeys.has(key);
                    return acc;
                }, {});
            }

            if (Array.isArray(data)) {
                const enabledKeys = new Set<string>(
                    (data as unknown[]).filter((x): x is string => typeof x === "string")
                );
                return keys.reduce<FeatureFlagsEnabledMap>((acc, key) => {
                    acc[key] = enabledKeys.has(key);
                    return acc;
                }, {});
            }

            return keys.reduce<FeatureFlagsEnabledMap>((acc, key) => {
                acc[key] = false;
                return acc;
            }, {});
        } catch (error) {
            console.error("Failed to get enabled feature flags:", error);
            return keys.reduce<FeatureFlagsEnabledMap>((acc, key) => {
                acc[key] = false;
                return acc;
            }, {});
        }
    }
}

export const featureFlagService = FeatureFlagService.getInstance();
